import { Group, House, LocationOn, PhotoCamera, PictureAsPdf } from '@mui/icons-material';
import { Box, Button, FormHelperText, Typography } from '@mui/material';
import { Form, Formik, FormikContextType } from 'formik';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiUsability, acceptedDoc, acceptedImage, acceptedVideo } from '../../../lib/generalTypes';
import { findGeolocationFromAddress, isSubmitEnabled } from '../../../lib/helperFuncs';
import DialogAction from '../../Dialog/DialogAction';
import { formInStepperBuilder, renderErrors } from '../../form_v2/helpers';
import { FieldControllerTypes, FieldTypes } from '../../form_v2/types';
import FormActionButtonLayout from '../../layout/Form/FormActionButtonLayout';
import FormLayout from '../../layout/Form/FormLayout';
import { VerticalStepper } from '../../Stepper';
import { ProfessionalFormProps } from './type';
import GMapsPlaceMarker from '../../Maps/GMapsPlaceMarker';
import { MapPoint } from '../../Maps/types';
import { CreateProfessionalValidation } from '../../../lib/static/professionals/validationSchema';
import { useApiState } from '../../../hooks/useApiState';
import HttpService from '../../../services/HttpService';
import UserService from '../../../services/UserService';
import i18n from '../../../i18n';
import { IntExt } from '../../../lib/permissions';

const CreateProfessionalSteps = ({
  initialValues,
  onSubmit, 
  edit=false,
  level1Options,
  languages,
  regions,
  regionsOnly,
  specialtiesLevel2,
  isLoading
}: ProfessionalFormProps) => {
      
  const [openSubmit, setOpenSubmit] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);

  const { apiEnabled, loadingApi } = useApiState(ApiUsability.Google_Maps, true);
  const { 
    apiEnabled: geoLocationApi,
    loadingApi: geoLocationLoading 
  } = useApiState(ApiUsability.Geolocation);

  const [geolocation, setGeolocation] = useState<MapPoint>({
    lat: initialValues.latitude
      ? Number(initialValues.latitude)
      : null,
    lng: initialValues.longitude
      ? Number(initialValues.longitude)
      : null
  });

  const [searchParams] = useSearchParams();

  const [geolocationError, setGeolocationError] = useState<string>('');

  const formRef = useRef<FormikContextType<any>>(null);
  const is_external = UserService.hasRole([IntExt.External]);

  const navigate = useNavigate();

  const { t } = useTranslation();

  // Steps and Form Fields 
  const step1Form = [
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Επωνυμία Επαγγελματία / Επιχείρησης'),
      name: 'title',
      required: true,
      disabled: UserService.hasRole(['EXTERNAL_TAXISNET']) && !searchParams.has('edit') && initialValues.title
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Επώνυμο'),
      name: 'last_name',
      required: true,
      disabled: UserService.hasRole(['EXTERNAL_TAXISNET']) && !searchParams.has('edit') && initialValues.last_name
    }], 
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Όνομα'),
      name: 'first_name',
      required: true,
      disabled: UserService.hasRole(['EXTERNAL_TAXISNET']) && !searchParams.has('edit') && initialValues.first_name
    },
    {
      control: FieldControllerTypes.telInput,
      label: t('Τηλέφωνο'),
      name: 'phone_number',
      required: true,
    }],
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('ΑΦΜ'),
      name: 'afm',
      required: true,
      disabled: UserService.hasRole(['EXTERNAL_TAXISNET']) && !searchParams.has('edit') && initialValues.afm
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Email'),
      name: 'email',
      required: true,
    }]
  ];
  const step2Form = [
    [{
      control: FieldControllerTypes.autocomplete,
      label: t('Κατηγορία Υπηρεσιών'),
      options: level1Options,
      name: 'category_level_1',
      customLabel: true,
      info: true,
      tooltip: t('Πρέπει να συμπληρώσετε τουλάχιστον ένα πεδίο, είτε την Κατηγορία Υπηρεσιών είτε την Ειδικότητα Επαγγελματία, για να συνεχίσετε στο επόμενο βήμα της φόρμας'),
    },
    {
      control: FieldControllerTypes.multiselecttree,
      label: t('Ειδικότητα Επαγγελματία'),
      name: 'specialty_lvl2',
      options: specialtiesLevel2,
      row: true,
      customLabel: true,
      isLoading: isLoading,
      info: true,
      tooltip: t('Πρέπει να συμπληρώσετε τουλάχιστον ένα πεδίο, είτε την Κατηγορία Υπηρεσιών είτε την Ειδικότητα Επαγγελματία, για να συνεχίσετε στο επόμενο βήμα της φόρμας'),
    }],
    ...(is_external 
      ? i18n.language !== "el"
        ? [ {
          control: FieldControllerTypes.input,
          type: 'search',
          label: t('Περιγραφή Προσφερόμενων Υπηρεσιών και Ειδικότητας'),
          placeholder: t('Εισάγετε πληροφορίες για τις υπηρεσίες που προσφέρετε, την ειδικότητα που κατέχετε, τα πλεονεκτήματα και τις δυνατότητες που παρέχετε.'),
          name: 'services_en',
          required: true
        }]
        : [
          {
            control: FieldControllerTypes.input,
            type: 'search',
            label: t('Περιγραφή Προσφερόμενων Υπηρεσιών και Ειδικότητας'),
            placeholder: t('Εισάγετε πληροφορίες για τις υπηρεσίες που προσφέρετε, την ειδικότητα που κατέχετε, τα πλεονεκτήματα και τις δυνατότητες που παρέχετε.'),
            name: 'services',
            required: true
          }
        ]
      : [
        {
          control: FieldControllerTypes.input,
          type: 'search',
          label: t('Περιγραφή Προσφερόμενων Υπηρεσιών και Ειδικότητας'),
          placeholder: t('Εισάγετε πληροφορίες για τις υπηρεσίες που προσφέρετε, την ειδικότητα που κατέχετε, τα πλεονεκτήματα και τις δυνατότητες που παρέχετε.'),
          name: 'services',
          required: true
        },
        {
          control: FieldControllerTypes.input,
          type: 'search',
          label: i18n.language !== "el"
            ? t('Περιγραφή Προσφερόμενων Υπηρεσιών και Ειδικότητας')
            : 'Περιγραφή Προσφερόμενων Υπηρεσιών και Ειδικότητας στα Αγγλικά' ,
          placeholder: i18n.language !== "el"
            ? t('Εισάγετε πληροφορίες για τις υπηρεσίες που προσφέρετε, την ειδικότητα που κατέχετε, τα πλεονεκτήματα και τις δυνατότητες που παρέχετε.')
            : 'Εισάγετε πληροφορίες για τις υπηρεσίες που προσφέρετε, την ειδικότητα που κατέχετε, τα πλεονεκτήματα και τις δυνατότητες που παρέχετε στα Αγγλικά' ,
          name: 'services_en',
          required: true
        }
      ]),
    ...(is_external 
      ? i18n.language !== "el"
        ? [{
          control: FieldControllerTypes.input,
          type: 'search',
          label: t('Παραγωγές που έχετε συμμετάσχει'),
          placeholder: t('Εισάγετε τις παραγωγές στις οποίες έχετε συμμετάσχει'),
          name: 'productions_en',
          required: true
        }]
        : [{
          control: FieldControllerTypes.input,
          type: 'search',
          label: t('Παραγωγές που έχετε συμμετάσχει'),
          placeholder: t('Εισάγετε τις παραγωγές στις οποίες έχετε συμμετάσχει'),
          name: 'productions',
          required: true
        }]
      : [
        {
          control: FieldControllerTypes.input,
          type: 'search',
          label: t('Παραγωγές που έχετε συμμετάσχει'),
          placeholder: t('Εισάγετε τις παραγωγές στις οποίες έχετε συμμετάσχει'),
          name: 'productions',
          required: true
        }, 
        {
          control: FieldControllerTypes.input,
          type: 'search',
          label: i18n.language !== "el"
            ? t('Παραγωγές που έχετε συμμετάσχει')
            : 'Παραγωγές που έχετε συμμετάσχει στα Αγγλικά' ,
          placeholder: i18n.language !== "el"
            ? t('Εισάγετε τις παραγωγές στις οποίες έχετε συμμετάσχει')
            : 'Εισάγετε τις παραγωγές στις οποίες έχετε συμμετάσχει στα Αγγλικά' ,
          name: 'productions_en',
          required: true
        }
      ])
    ,
    [{
      control: FieldControllerTypes.autocomplete,
      label: t('Γλώσσες Επικοινωνίας'),
      name: 'languages',
      customLabel: true,
      options: languages,
      required: true
    },
    {
      control: FieldControllerTypes.multiselectall,
      label: t('Περιοχές Εξυπηρέτησης / Παροχής Υπηρεσιών'),
      name: 'regions',
      options: regionsOnly,
      customLabel: true,
      required: true,
      selectAllLabel: t('Πανελλαδικά')
    }],
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Website'),
      name: 'website',
      placeholder: 'www.website.com'
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('LinkedIn'),
      name: 'linkedin',
      placeholder: 'https://www.linkedin.com/in/my-account/'
    }],
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Facebook'),
      name: 'facebook',
      placeholder: 'https://www.facebook.com/myaccount'
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Instagram'),
      name: 'instagram',
      placeholder: 'https://www.instagram.com/myaccount/'
    }],
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Youtube'),
      name: 'youtube',
      placeholder: 'https://www.youtube.com/channel/myaccount'
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Vimeo'),
      name: 'vimeo',
      placeholder: 'https://vimeo.com/user012345678'
    }]
  ];
  const step3Form = [
    [{
      control: FieldControllerTypes.autoselect,
      label: t('Περιφέρειες / Δήμοι'),
      name: 'city',
      options: regions,
      required: true,
      customLabel: true
    },
    {
      control: FieldControllerTypes.input,
      type: FieldTypes.number,
      label: t('ΤΚ'),
      name: 'tk',
      required: true
    }],
    [{
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Οδός'),
      name: 'address',
      required: true
    },
    {
      control: FieldControllerTypes.input,
      type: 'search',
      label: t('Αριθμός'),
      name: 'address_number',
      required: true
    }],
    (apiEnabled && !loadingApi) || (geoLocationApi && !geoLocationLoading)
      ? <Box my={2} key='map-form'>
        {(geoLocationApi && !geoLocationLoading)
          ? <Box display='flex' my={2}
            justifyContent={'flex-end'}>
            <Button variant='contained' 
              onClick={async () => {
                try {
                  formRef?.current?.setFieldTouched('location', true);
                  const foundLocation = await findGeolocationFromAddress(
                    `
            ${formRef?.current?.values.address || ''} 
            ${formRef?.current?.values.address_number || ''} 
            ${formRef?.current?.values.tk || ''}`,
                  );
                  await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/crm/api/${ApiUsability.Geolocation}`);
                  setGeolocation(foundLocation);
                  if (foundLocation?.lat == 0 && foundLocation?.lng == 0) {
                    setGeolocationError('Δεν βρέθηκε τοποθεσία');
                  }
                  formRef?.current?.registerField('location', {
                    validate: () => foundLocation?.lat == 0 && foundLocation?.lng == 0
                      ? 'Δεν βρέθηκε τοποθεσία'
                      : ''
                  });
                  if (foundLocation?.lat !== 0 && foundLocation?.lng !== 0) {
                    formRef?.current?.setFieldError('location', undefined);
                    setGeolocationError('');
                  }

                  formRef?.current?.setFieldValue('location', foundLocation);

                } catch (error) {
                  console.error(error);
                }
              }
              }>
              {t('Αναζήτηση Τοποθεσίας')}
            </Button>
          </Box>
          : null
        }
        {(apiEnabled && !loadingApi)
          ? <GMapsPlaceMarker
            height='450px'
            zoom={17}
            location={
              (geolocation.lat && geolocation.lng)
                ? {
                  lat: Number(geolocation.lat),
                  lng: Number(geolocation.lng)
                }
                : undefined
            }
          />
          : null
        }
        {geolocationError && <FormHelperText
          error>
          <Typography
            variant='body1'
            sx={{
              marginTop: '16px',
              fontWeight: 'bolder',
            }}>
            {geolocationError}
          </Typography>
        </FormHelperText>
        }
      </Box >
      : []
  ];
  const step4Form = [
    {
      control: FieldControllerTypes.file,
      type: FieldTypes.file,
      label: t('Προσθήκη Φωτογραφίας Εξωφύλλου'),
      name: 'thumbnail',
      accepts: [...acceptedImage]
    }, 
    {
      control: FieldControllerTypes.file,
      type: FieldTypes.file,
      label: t('Προαιρετική Προσθήκη Φωτογραφιών/Βίντεο'),
      name: 'media',
      multiple: true,
      nofiles: 10,
      accepts: [...acceptedImage, ...acceptedVideo]
    },
  ];
  const step5Form = [
    {
      control: FieldControllerTypes.file,
      type: FieldTypes.file,
      label: t('Προσθήκη Βιογραφικού Σημειώματος (PDF)'),
      name: 'resume',
      accepts: [...acceptedDoc]
    },
    {
      control: FieldControllerTypes.file,
      type: FieldTypes.file,
      label: t('Άλλα Επισυναπτόμενα Έγγραφα (PDF)'),
      name: 'attached_files',
      multiple: true,
      nofiles: 10,
      accepts: [...acceptedDoc]
    }
  ];

  const step1 = {
    label: t('Βασικά Στοιχεία Επαγγελματία / Επιχείρησης'),
    content: step1Form,
    icon: <House />
  };
  const step2 = {
    label: t('Λοιπά Στοιχεία Επιχείρησης / Επαγγελματία'),
    content: step2Form,
    icon: <Group />
  };
  const step3 = {
    label: t('Στοιχεία Έδρας Επιχείρησης'),
    content: step3Form,
    icon: <LocationOn />
  };
  const step4 = {
    label: t('Φωτογραφίες / Βίντεο'),
    content: step4Form,
    icon: <PhotoCamera />,
  };
  const step5 = {
    label: t('Επισυναπτόμενα Έγγραφα'),
    content: step5Form,
    icon: <PictureAsPdf />
  };

  const wholeForm = [
    {...step1},
    {...step2},
    {...step3},
    {...step4},
    {...step5}
  ];

  const errorsArray = [
    ...[
      {
        name: t('Επωνυμία Επαγγελματία / Επιχείρησης'),
        value: '',
        refValue: 'title'
      },
      {
        name: t('ΑΦΜ'),
        value: '',
        refValue: 'afm'
      },
      {
        name: t('Κατηγορία Υπηρεσιών'),
        value: '',
        refValue: 'category_level_1'
      },
      {
        name: t('Κλάδος Ειδικότητας'),
        value: '',
        refValue: 'specialty'
      },
      {
        name: t('Ειδικότητα Επαγγελματία'),
        value: '',
        refValue: 'specialty_lvl2'
      },
      {
        name: t('Περιγραφή Προσφερόμενων Υπηρεσιών'),
        value: '',
        refValue: 'services'
      },
      {
        name: t('Παραγωγές που έχετε συμμετάσχει'),
        value: '',
        refValue: 'productions'
      },
      {
        name: t('Γλώσσες Επικοινωνίας'),
        value: '',
        refValue: 'languages'
      },
      {
        name: t('Περιοχές Εξυπηρέτησης / Παροχής Υπηρεσιών'),
        value: '',
        refValue: 'regions'
      }
    ],
    ...[
      
      {
        name: t('Website'),
        value: '',
        refValue: 'website'
      },
      {
        name: t('Facebook'),
        value: '',
        refValue: 'facebook'
      },
      {
        name: t('YouTube'),
        value: '',
        refValue: 'youtube'
      },
      {
        name: t('LinkedIn'),
        value: '',
        refValue: 'linkedin'
      },
      {
        name: t('Instagram'),
        value: '',
        refValue: 'instagram'
      },
      {
        name: t('Vimeo'),
        value: '',
        refValue: 'vimeo'
      }
    ],
    ...[
      {
        name: t('Περιφέρεια / Δήμος'),
        value: '',
        refValue: 'city'
      },
      {
        name: t('ΤΚ'),
        value: '',
        refValue: 'tk'
      },
      {
        name: t('Οδός'),
        value: '',
        refValue: 'address'
      },
      {
        name: t('Αριθμός'),
        value: '',
        refValue: 'address_number'
      },
    ],
    ...[
      {
        name: t('Όνομα'),
        value: '',
        refValue: 'first_name'
      }, 
      {
        name: t('Επώνυμο'),
        value: '',
        refValue: 'last_name'
      }, 
      {
        name: t('Τηλέφωνο'),
        value: '',
        refValue: 'phone_number'
      }, 
      {
        name: 'Email',
        value: '',
        refValue: 'email'
      }
    ],
    ...[
      {
        name: t('Φωτογραφία Εξωφύλλου'),
        value: '',
        refValue: 'thumbnail'
      },
      {
        name: t('Φωτογραφίες/Βίντεο'),
        value: '',
        refValue: 'media'
      },
      {
        name: t('Βιογραφικό Σημείωμα'),
        value: '',
        refValue: 'resume'
      },
      {
        name: t('Επισυναπτόμενα Έγγραφα'),
        value: '',
        refValue: 'attached_files'
      },
    ],
    ...[
      {
        name: t('Επιβεβαίωση Διεύθυνσης'),
        value: '',
        refValue: 'location'
      }
    ]
  ];

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={() => undefined}
        initialValues={initialValues}
        validationSchema={CreateProfessionalValidation}
        validateOnBlur={false}
        innerRef={formRef}
      >
        {formik =>
          (<>
            <Form>
              <FormLayout>
                <VerticalStepper activeStep={1}>
                  {formInStepperBuilder(wholeForm, formik, !edit)}
                </VerticalStepper>
              </FormLayout>
              {edit
                ? <Box ml={2} mt={3}>
                  {renderErrors(formik.errors, errorsArray)}
                </Box>
                : null
              }
              <FormActionButtonLayout>
                <Button variant='contained'
                  disabled={isSubmitEnabled(formik?.errors,
                    searchParams.has('edit')
                      ? true
                      : formik?.dirty,
                    ['title', 'first_name', 'last_name', 'email', 'phone_number', 'afm', 'thumbnail', 'media', 'resume', 'attached_files']
                  )}
                  onClick={() => setOpenSubmit(true)}> 
                  {edit
                    ? t('Αποθήκευση')
                    : t('Αποθήκευση και Προεπισκόπηση')
                  }
                </Button>
                <DialogAction open={openCancel} handleClose={() => setOpenCancel(false)}
                  title={String(t('Ακύρωση Δημιουργίας Επαγγελματία / Επιχείρησης'))} 
                  content={edit
                    ? t('Είστε σίγουρος/η ότι θέλετε να ακυρώσετε την επεξεργασία του Επαγγελματία / Επιχείρησης;')
                    : t('Ειστε σιγουρος/η ότι θέλετε να ακυρώσετε την καταχώριση Επαγγελματία / Επιχείρησης;')
                  }
                  onAccept={() => {
                    setOpenCancel(false); 
                    navigate(-1);
                  }}/>
                <DialogAction open={openSubmit} handleClose={() => setOpenSubmit(false)}
                  title={edit 
                    ? String(t('Επιβεβαίωση Επεξεργασίας'))
                    : String(t('Επιβεβαίωση Αποθήκευσης'))
                  } 
                  content={edit
                    ? t('Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την επεξεργασία του Χρήστη;')
                    : t('Είστε σίγουρος/η ότι θέλετε να προχωρήσετε με την καταχώριση Επαγγελματία / Επιχείρησης;')}
                  onAccept={() => {
                    onSubmit(formik.values, geolocation);
                    setOpenSubmit(false);
                  }}/>
              </FormActionButtonLayout>
            </Form>
          </>)
        }
      </Formik>
    </>
  );
};

export default CreateProfessionalSteps;